import { useState, useEffect } from 'react';
import { Star, User, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const Reviews = () => {
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    comment: '',
  });

  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Avis par défaut (affichés si la base de données est vide)
  const defaultReviews = [
    {
      name: 'Alexandre D.',
      rating: 5,
      date: 'Il y a 2 semaines',
      comment: 'Service exceptionnel ! Ma Mercedes n’a jamais été aussi brillante. Le polissage est impeccable, aucune trace, aucun hologramme. Je recommande vivement SUKI à tous les passionnés d’automobile.',
      verified: true,
    },
    {
      name: 'Sophie M.',
      rating: 5,
      date: 'Il y a 3 semaines',
      comment: 'Un travail d’une précision remarquable. L’équipe est professionnelle et passionnée. La protection céramique a transformé ma voiture. Elle brille comme au premier jour et l’eau perle à la perfection.',
      verified: true,
    },
    {
      name: 'Thomas R.',
      rating: 5,
      date: 'Il y a 1 mois',
      comment: 'Je suis venu pour une correction de peinture et le résultat dépasse mes attentes. Les rayures ont disparu et la profondeur de la couleur est incroyable. Un vrai savoir-faire ! SUKI est une référence à Montmagny.',
      verified: true,
    },
    {
      name: 'Marie L.',
      rating: 5,
      date: 'Il y a 1 mois',
      comment: 'Attention aux détails incroyable. Ils ont pris le temps de tout expliquer et le résultat est juste parfait. Mon Audi brille comme au premier jour. Le service client est au top !',
      verified: true,
    },
    {
      name: 'Pierre B.',
      rating: 5,
      date: 'Il y a 2 mois',
      comment: 'Après avoir essayé plusieurs detailers à Montmagny, j’ai enfin trouvé SUKI. Le niveau de finition est incomparable. Mon BMW M4 n’a jamais été aussi impeccable. Je ne vais plus ailleurs !',
      verified: true,
    },
    {
      name: 'Camille D.',
      rating: 5,
      date: 'Il y a 2 mois',
      comment: 'Travail minutieux et soigné. J’ai opté pour le forfait Platinum et je ne regrette rien. La protection céramique fait toute la différence. Excellent rapport qualité-prix pour la qualité du service.',
      verified: true,
    },
    {
      name: 'Lucas M.',
      rating: 5,
      date: 'Il y a 3 mois',
      comment: 'Professionnel, rapide et efficace. Ma Porsche 911 a retrouvé tout son éclat. L’équipe est passionnée et ça se voit dans le résultat final. Merci SUKI !',
      verified: true,
    },
    {
      name: 'Émilie K.',
      rating: 5,
      date: 'Il y a 3 mois',
      comment: 'Je suis venue avec ma Mini Cooper toute terne. Ils ont fait des miracles ! Le polissage et la protection nano ont redonné vie à ma voiture. Accueil chaleureux et service impeccable.',
      verified: true,
    },
  ];

  // Charger les avis depuis l'API
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { getReviews } = await import('@/utils/api');
        const apiReviews = await getReviews();
        
        // Formater les avis de l'API
        const formattedReviews = apiReviews.map(review => ({
          name: review.name,
          rating: review.rating,
          date: formatDate(review.created_at),
          comment: review.comment,
          verified: true,
        }));
        
        // Combiner les avis de l'API avec les avis par défaut
        if (formattedReviews.length > 0) {
          setReviews([...formattedReviews, ...defaultReviews]);
        } else {
          setReviews(defaultReviews);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews(defaultReviews);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    if (diffDays < 30) return `Il y a ${Math.ceil(diffDays / 7)} semaine${Math.ceil(diffDays / 7) > 1 ? 's' : ''}`;
    if (diffDays < 365) return `Il y a ${Math.ceil(diffDays / 30)} mois`;
    return `Il y a ${Math.ceil(diffDays / 365)} an${Math.ceil(diffDays / 365) > 1 ? 's' : ''}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.comment) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const { createReview } = await import('@/utils/api');
      await createReview(formData);
      
      toast.success('Merci pour votre avis ! Il sera publié après modération.');
      
      // Reset form
      setFormData({
        name: '',
        rating: 5,
        comment: '',
      });
    } catch (error) {
      console.error('Review error:', error);
      toast.error('Erreur lors de l\'envoi de l\'avis. Veuillez réessayer.');
    }
  };

  const renderStars = (rating, interactive = false, size = 20) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={size}
            className={`${
              (interactive ? hoveredRating || formData.rating : rating) >= star
                ? 'text-[#c9a961] fill-current'
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer transition-colors' : ''}`}
            onClick={interactive ? () => setFormData(prev => ({ ...prev, rating: star })) : undefined}
            onMouseEnter={interactive ? () => setHoveredRating(star) : undefined}
            onMouseLeave={interactive ? () => setHoveredRating(0) : undefined}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-20 pb-20" data-testid="reviews-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
        <div className="relative z-10 text-center text-white px-4">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="text-[#c9a961]" size={24} />
            <span className="text-[#c9a961] font-medium tracking-wider uppercase">Témoignages</span>
          </div>
          <h1 className="hero-title text-5xl md:text-7xl mb-4">Avis Clients</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Découvrez ce que nos clients disent de leur expérience SUKI
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-[#c9a961] mb-2 section-title">5.0</div>
              <div className="flex justify-center mb-2">{renderStars(5, false, 24)}</div>
              <p className="text-gray-600">Note moyenne</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#c9a961] mb-2 section-title">200+</div>
              <p className="text-gray-600 mt-4">Clients satisfaits</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-[#c9a961] mb-2 section-title">100%</div>
              <p className="text-gray-600 mt-4">Recommandations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl mb-4 text-[#1a1a1a] gold-line inline-block pb-4">
              Ce qu'ils disent de nous
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-none"
                data-testid={`review-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-[#c9a961]/20 flex items-center justify-center">
                        <User className="text-[#c9a961]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{review.name}</h3>
                        <p className="text-xs text-gray-500">{review.date}</p>
                      </div>
                    </div>
                    {review.verified && (
                      <div className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                        Vérifié
                      </div>
                    )}
                  </div>
                  <div className="mb-3">{renderStars(review.rating)}</div>
                  <p className="text-gray-500 leading-relaxed">{review.comment}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Review Form */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title text-4xl md:text-5xl mb-4 text-[#1a1a1a] gold-line inline-block pb-4">
              Laissez votre avis
            </h2>
            <p className="text-gray-600 text-lg">
              Votre expérience compte pour nous
            </p>
          </div>

          <Card className="shadow-xl border-none">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="review-name" className="text-base">Votre nom *</Label>
                  <Input
                    id="review-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    required
                    data-testid="review-name-input"
                    className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-base">Votre note *</Label>
                  <div className="flex items-center space-x-4">
                    {renderStars(formData.rating, true, 32)}
                    <span className="text-lg font-semibold text-[#c9a961]">{formData.rating}/5</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="review-comment" className="text-base">Votre commentaire *</Label>
                  <Textarea
                    id="review-comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    placeholder="Partagez votre expérience avec SUKI..."
                    rows={6}
                    required
                    data-testid="review-comment-textarea"
                    className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="review-submit-button"
                  className="w-full bg-[#c9a961] hover:bg-[#b8984e] text-white text-lg py-6 rounded-full font-semibold transition-all duration-300"
                >
                  Publier mon avis
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
